import { Component } from '@theme/component';
import { addToCart } from '@theme/cart';
import { ThemeEvents } from '@theme/events';
import { announce, t } from '@theme/utilities';

/**
 * Adds to cart without leaving the page.
 *
 * The form is a real `{% form 'product' %}` with a real submit button, so it
 * still works with JavaScript unavailable or still loading. This only
 * intercepts the submit.
 *
 * @extends {Component}
 */
class ProductFormComponent extends Component {
  requiredRefs = ['submit'];

  /**
   * @param {SubmitEvent} event
   */
  submit = async (event) => {
    const form = event.target;
    const button = /** @type {HTMLButtonElement} */ (this.refs.submit);

    if (!(form instanceof HTMLFormElement) || button.disabled) return;

    // The dynamic checkout button Shopify renders sits inside this same form.
    // Cancelling every submit would swallow it, so anything submitted by a
    // control other than one of ours is left to its own flow.
    //
    // "Ours" is more than `refs.submit`: the sticky buy bar lives outside this
    // component and submits the form remotely through its `form` attribute,
    // so it identifies itself with `data-primary-submit` instead.
    const submitter = event.submitter;
    const isOurs =
      !submitter || submitter === button || submitter.hasAttribute('data-primary-submit');

    if (!isOurs) return;

    event.preventDefault();

    button.disabled = true;
    this.setAttribute('loading', '');
    this.#setError('');

    try {
      await addToCart(new FormData(form), 'product-form');

      announce(t('added'));

      // Opening the drawer is the confirmation. Without one the shopper gets
      // no feedback at all that the click did anything.
      if (this.hasAttribute('open-drawer')) {
        const drawer = document.getElementById('cart-drawer');
        if (drawer && 'open' in drawer) /** @type {any} */ (drawer).open();
      }
    } catch (error) {
      this.#setError(/** @type {Error} */ (error).message);
    } finally {
      button.disabled = false;
      this.removeAttribute('loading');
    }
  };

  /**
   * @param {string} message
   */
  #setError(message) {
    const target = this.refs.error;
    if (!(target instanceof HTMLElement)) return;

    target.textContent = message;
    target.toggleAttribute('hidden', !message);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartError, this.#onCartError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartError, this.#onCartError);
  }

  /** @param {Event} event */
  #onCartError = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.source === 'product-form') this.#setError(detail.message);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
